// Standalone entry — daemon.skenmy.com.
//
// This is the *whole* page. There is no skenOS here: no WM, no dock, no
// menubar, no app registry, no styles.css. That is not an aesthetic
// choice, it's the point of the build target — a shared link to a game
// should open a game.
//
// Two rules this file exists to enforce:
//
//   1. **Nothing from `src/shell` or `src/apps`.** Importing one app
//      pulls `apps-registry`, which pulls every app that registered
//      itself, which pulls marked + shiki. That is the entire skenOS
//      bundle arriving to render a canvas. Type-only imports are fine
//      (erased); value imports are not.
//
//   2. **Never block on the world.** The boot frame in daemon.html
//      paints before a byte of JS runs; React swaps in as soon as it
//      parses; the game chunk arrives behind `lazy()` and takes over
//      when it's ready. At no point is the visitor looking at nothing.
//      The single most-repeated criticism of game-like sites is a slow
//      blank load, and it is entirely avoidable.

// Imported by the entry, not only by the component.
//
// `DaemonGame` is reached by both HTML entries, so Vite treats its
// stylesheet as belonging to the shared chunk and attaches it to the
// *index* entry — leaving this page with no <link> at all and the
// game unstyled. Importing it here makes it part of the standalone
// entry's own CSS, so Vite emits a real <link> into daemon.html at
// build time. That also beats runtime injection for first paint.
import "./lib/daemon/daemon.css";
import { StrictMode, Suspense, lazy, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { standaloneHost, onSay, type SayLine } from "./lib/daemon/hosts";
import { installUmami } from "./lib/umami";
import type { NiceLevel, RunConfig } from "./lib/daemon/types";

// Code-split the game away from the shell. The shell is a few KB and
// paints instantly; the sim + renderer can take as long as they need.
const DaemonGame = lazy(async () => {
  const mod = await import("./lib/daemon/DaemonGame");
  return { default: mod.DaemonGame };
});

// The claim page is a different page that happens to live in the same
// bundle. Split so the game never pays for it: almost nobody who opens
// this host is here to claim, and the ones who are arrive on a phone.
const ClaimPage = lazy(async () => {
  const mod = await import("./lib/daemon/ClaimPage");
  return { default: mod.ClaimPage };
});

const LeaderboardPage = lazy(async () => {
  const mod = await import("./lib/daemon/LeaderboardPanel");
  return { default: mod.LeaderboardPanel };
});

/**
 * Caddy serves `daemon.html` for every path on this host (`try_files
 * {path} /daemon.html`), so routing is ours to do.
 *
 * Three routes, matched on the pathname, and anything unrecognised is
 * the game — a bad URL should drop you into DAEMON rather than a 404,
 * because there is nothing else here worth showing you.
 */
export type Route = "game" | "claim" | "board";

export function routeFor(pathname: string): Route {
  switch (pathname.replace(/\/+$/, "").toLowerCase()) {
    case "/claim":
      return "claim";
    case "/board":
      return "board";
    default:
      return "game";
  }
}

/** Kept as a named export because the routing test and older callers
 *  read better with it, and it is one expression. */
export const isClaimPath = (pathname: string): boolean => routeFor(pathname) === "claim";

// ── Run configuration from the URL ────────────────────────────────────

/** Valid `nice` levels, meanest last. Anything else in the URL is
 *  ignored rather than clamped — a typo shouldn't silently hand someone
 *  a different difficulty than the one they shared. */
const NICE_LEVELS: readonly NiceLevel[] = [19, 10, 5, 0, -5, -10, -15, -20];

/**
 * Default difficulty: the kindest rung.
 *
 * Was `nice 0` — "the priority an ordinary process runs at", which is
 * the honest name for normal and the wrong place to put someone who has
 * never played a bullet hell. This is a link people are handed at an
 * event, on a machine they do not own, with a queue behind them. The
 * first thirty seconds decide whether they play at all, and there is a
 * difficulty picker on the intro for anyone who wants a harder one.
 *
 * `+19` is maximum niceness — DAEMON being maximally considerate —
 * which is the joke working in the player's favour for once.
 */
const DEFAULT_NICE: NiceLevel = 19;

/**
 * The daily seed. UTC date, so everyone racing the same day races the
 * same fight regardless of timezone, and so the string is stable to
 * paste at someone.
 */
export function dailySeed(now: Date = new Date()): string {
  return `daily-${now.toISOString().slice(0, 10)}`;
}

/**
 * Build a run config from a query string.
 *
 * `?seed=` is the shareable one — "beat my time on this exact fight".
 * `?nice=` rides along so a shared link can carry its difficulty; a
 * seed without a nice is a normal-difficulty run.
 *
 * A run is `daily` only when no explicit seed was given, because that's
 * the run whose records go on the daily board. Handing someone a seed
 * link should not let them post a daily time.
 */
export function configFromQuery(search: string, now: Date = new Date()): RunConfig {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    params = new URLSearchParams();
  }

  const raw = params.get("seed");
  // Cap the length: the seed ends up in the DOM and in storage keys, and
  // there's no reason for a legitimate one to be long.
  const seed = raw && raw.trim() ? raw.trim().slice(0, 64) : null;

  const niceRaw = params.get("nice");
  const niceNum = niceRaw === null ? Number.NaN : Number(niceRaw);
  const nice = NICE_LEVELS.find((n) => n === niceNum) ?? DEFAULT_NICE;

  return {
    seed: seed ?? dailySeed(now),
    nice,
    modifier: null,
    daily: seed === null,
  };
}

// ── The say overlay ───────────────────────────────────────────────────
//
// skenOS routes `host.say` through the WM's toast system. There is no WM
// here, so the page draws the line itself — same channel, different
// surface. `hosts.tsx` publishes; this subscribes.

const SAY_TTL_MS = 4200;
const SAY_TTL_BOSS_MS = 6500;

function SayOverlay(): JSX.Element | null {
  const [line, setLine] = useState<SayLine | null>(null);

  useEffect(() => {
    let timer = 0;
    const off = onSay((next) => {
      setLine(next);
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => setLine((cur) => (cur === next ? null : cur)),
        next.tone === "boss" ? SAY_TTL_BOSS_MS : SAY_TTL_MS,
      );
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);

  if (!line) return null;
  return (
    <div className="say" data-tone={line.tone} role="status" aria-live="polite">
      {line.line}
    </div>
  );
}

// ── The walls ─────────────────────────────────────────────────────────
//
// The runlevel-2 shrink device. Inside skenOS the real WindowFrame
// closes in, which is the best thing in the design and only possible
// because the game lives in a fake OS. Out here there is no frame, so
// the page draws the walls instead: same scale, same numbers, different
// expression. Driven entirely by the `--daemon-arena-scale` custom
// property that `hosts.tsx` writes — no state, no re-render, no rAF.

function Walls(): JSX.Element {
  return <div className="walls" aria-hidden="true" />;
}

// ── Boot frame ────────────────────────────────────────────────────────
//
// Visually identical to the static one in daemon.html, so the handoff
// from "HTML painted" to "React mounted, chunk pending" is invisible.

function Boot(): JSX.Element {
  return (
    <div className="boot">
      <div className="boot-mark" />
      <p className="boot-line">
        spawning <strong>DAEMON</strong>…
      </p>
    </div>
  );
}

// ── Mount ─────────────────────────────────────────────────────────────

export function StandaloneApp({
  config,
  route = "game",
}: {
  config: RunConfig;
  route?: Route;
}): JSX.Element {
  if (route === "claim") {
    return (
      <div className="stage stage-claim">
        <Suspense fallback={<Boot />}>
          <ClaimPage />
        </Suspense>
      </div>
    );
  }
  if (route === "board") {
    // The board on its own page, with no run behind it. Shareable, and
    // the thing you send someone when the answer to "what did you get?"
    // needs a link rather than a screenshot.
    return (
      <div className="stage stage-board">
        <Suspense fallback={<Boot />}>
          <LeaderboardPage initialNice={config.nice} seed={config.seed} />
        </Suspense>
      </div>
    );
  }
  return (
    <div className="stage">
      <Suspense fallback={<Boot />}>
        <DaemonGame host={standaloneHost} config={config} />
      </Suspense>
      <Walls />
      <SayOverlay />
    </div>
  );
}

function mount(): void {
  const el = document.getElementById("root");
  if (!el) throw new Error("root element missing");
  const config = configFromQuery(window.location.search);
  const route = routeFor(window.location.pathname);
  // Handy for "what seed was that?" without opening devtools, and it
  // gives the e2e smoke test something to assert on.
  document.documentElement.dataset.daemonSeed = config.seed;
  createRoot(el).render(
    <StrictMode>
      <StandaloneApp config={config} route={route} />
    </StrictMode>,
  );
}

// Guarded so the module can be imported by tests without mounting.
if (typeof document !== "undefined" && document.getElementById("root")) mount();

// Deferred deliberately: the tracker must never sit in front of first
// paint, which is the one thing this page is optimised for.
if (typeof window !== "undefined") {
  window.requestAnimationFrame(() => installUmami());
}
